import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  type IntegrationModuleForModuleItem,
  integrationsApi,
} from "@/features/integrations/api/integrations.api";

export type TenantIntegrationModuleForInvoice = {
  /** Primary ID for tenant page URLs. Use for navigation and getRowId. */
  tenantIntegrationModuleId: string;
  integrationModuleId: string;
  name: string;
  integrationName: string;
  openInvoiceCount: number;
};

type UseTenantIntegrationModulesForInvoicesParams = {
  tenantId?: string | null;
  page: number;
  pageSize: number;
  search?: string;
};

/**
 * Fetches integration modules for the invoice list page (campaigns module only).
 * GET /api/v2/Campaigns/Invoices/Integrations (paginated).
 */
export function useTenantIntegrationModulesForInvoices(
  params: UseTenantIntegrationModulesForInvoicesParams
) {
  const { tenantId, page, pageSize, search } = params;

  return useQuery({
    queryKey: [
      "integrationModules",
      "invoices",
      tenantId,
      page,
      pageSize,
      search,
    ],
    queryFn: () =>
      integrationsApi.fetchCampaignInvoiceIntegrations({
        tenantId: tenantId || undefined,
        page,
        pageSize,
        search: search || undefined,
      }),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    refetchOnMount: true,
    staleTime: 20 * 60 * 1000,
    enabled: true,
  });
}

export function mapInvoiceIntegrationModulesToRows(
  items: IntegrationModuleForModuleItem[] | undefined
): TenantIntegrationModuleForInvoice[] {
  return (items ?? []).map((i) => ({
    tenantIntegrationModuleId:
      i.tenantIntegrationModuleId ?? i.integrationModuleId,
    integrationModuleId: i.integrationModuleId,
    name: i.displayName || i.integrationName,
    integrationName: i.integrationName,
    openInvoiceCount: i.openInvoiceCount ?? 0,
  }));
}
